// Sitting on a sofa and lying in a bed (2026-10-29): the layered avatar in a pose, built from the pose atlases of
// scripts/anim/pose (one body atlas per character, one per garment, frame '0' = sit, '1' = lie). Pure, so the
// rules about which layers show are tested without Phaser; NatalkaSprite draws what this returns.
import { CLOTH_SLOTS, zOrderFor } from './clothSlots.mjs';
import { hairKeys } from '../look/hairArt.mjs';

// Mirrors scripts/anim/pose/layout.py (the test pins them equal).
export const POSE = {
  cell: { w: 512, h: 512 },
  frames: { sit: '0', lie: '1' },
  // Where the figure is pinned in its cell: sitting, the bottom middle of the feet; lying, the middle of the box.
  anchor: { sit: { x: 256, y: 496 }, lie: { x: 256, y: 256 } },
  // Garment slots that show lying in bed: none (the sheet is up to the chin; hats come off in bed). The hair always
  // shows: it is the body's, not a garment.
  lieSlots: [],
};

// The templates drew the standing figure 300 px tall and she is 240 on screen, so a pose cell is drawn at 0.8.
export const POSE_SCALE = 0.8;

// The top of the head, relative to the pose anchor at scale 1 (standing it is 120 px above the centre): where the
// Zzz, the name tag and a chat bubble go. Provisional — fitted against the painted poses.
export const STANDING_HEAD_TOP = { x: 0, y: -120 };
export const POSE_HEAD_TOP = { sit: { x: 0, y: -185 }, lie: { x: -110, y: -50 } };
export const headTop = (pose) => POSE_HEAD_TOP[pose] ?? STANDING_HEAD_TOP;

export const poseKey = (char, itemId) => (itemId ? `${char}_pose_${itemId}` : `${char}_pose`);

const LAYER_OF = Object.fromEntries(CLOTH_SLOTS.map((s) => [s.id, s.layer]));

/** The atlases a posed avatar needs: the body, every worn item, then the hair (and the default's neck). */
export function poseKeys(char, equipped, look = null) {
  const hair = hairKeys('pose', char, look, equipped);
  return [poseKey(char), ...Object.values(equipped ?? {}).filter(Boolean).map((id) => poseKey(char, id)), hair.front, hair.back,
    ...(hair.neck ? [hair.neck] : [])];
}

/**
 * What to draw, bottom to top: [{key, frame, layer}]. `has(key, frame)` says whether that atlas is loaded with
 * that frame. No body frame → nothing (the caller waits for it); a garment without its frame is left off. Drawn
 * mirrored (`flip`), a one-sided style's hair takes its own right-facing frame where its atlas has it: the frame after
 * the sit and lie ones ('2' / '3'), stored mirrored back so the flipped pose shows it facing right.
 */
export function poseLayers({ char, pose, equipped, look = null, has, flip = false }) {
  const frame = POSE.frames[pose];
  const body = poseKey(char);
  if (frame == null || !has(body, frame)) return [];
  const hair = hairKeys('pose', char, look, pose === 'lie' ? null : equipped);     // no hat in bed: the style's own
  const keyOf = { base: body, neck: hair.neck, hair: hair.front, hair_back: hair.back };
  const right = String(Number(frame) + Object.keys(POSE.frames).length);
  const hairFrame = flip && has(hair.front, right) ? right : frame;
  const itemOn = {};
  for (const [cat, id] of Object.entries(equipped ?? {})) if (id && LAYER_OF[cat]) itemOn[LAYER_OF[cat]] = id;
  const out = [];
  for (const layer of zOrderFor(char)) {
    let key = keyOf[layer];
    if (!key) {
      const id = itemOn[layer];
      if (!id || (pose === 'lie' && !POSE.lieSlots.includes(layer))) continue;
      key = poseKey(char, id);
    }
    const f = layer === 'hair' || layer === 'hair_back' ? hairFrame : frame;
    if (has(key, f)) out.push({ key, frame: f, layer });
  }
  return out;
}

/** The Zzz: always while lying in bed, otherwise when the player has gone idle. */
export const sleepShown = ({ idle, lying }) => !!(idle || lying);
